import {
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useMediaQuery } from "@/hooks/use-media-query";

type ActiveField = "treatment" | "location" | "when" | null;

export function useSearchBarInteraction(onSubmit: (fromPointer?: boolean) => void) {
  const isMobile = !useMediaQuery("(min-width: 768px)");
  const [activeField, setActiveField] = useState<ActiveField>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!activeField) return;
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setActiveField(null);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") setActiveField(null);
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [activeField]);

  const handleFieldClick = useCallback((field: ActiveField) => {
    setActiveField((prev) => (prev === field ? null : field));
  }, []);

  const handleSearchSubmit = useCallback(
    (event?: ReactMouseEvent<HTMLButtonElement>) => {
      setActiveField(null);
      onSubmit((event?.detail ?? 0) > 0);
    },
    [onSubmit],
  );

  const advanceField = useCallback(
    (next: ActiveField) => {
      if (isMobile) return;
      setActiveField(next);
    },
    [isMobile],
  );

  return {
    isMobile,
    activeField,
    setActiveField,
    containerRef,
    handleFieldClick,
    handleSearchSubmit,
    advanceField,
  };
}
